/**
 * Barcode Format: R[YY][B][MM][C]M[CS1][CS2]
 * Example: R2520160M0087
 * - R: Fixed prefix
 * - YY: Last 2 digits of year (25 = 2025)
 * - S: Single digit B (1-9)
 * - MM: Zero-padded month (01-12)
 * - BB: Bi-digit C (11-99)
 * - M: Fixed separator
 * - CS1: First checksum digit = (B * C * Series) % 100
 * - CS2: Second checksum digit = (B + C + Series) % 100
 * 
 * Series = floor(YY / MM)
 */

export interface BarcodeValidationResult {
  valid: boolean;
  error?: string;
  components?: {
    year: string;
    singleDigit: string;
    month: string;
    biDigit: string;
    checksum1: string;
    checksum2: string;
  };
}

/**
 * Validate barcode format using regex
 */
function validateFormat(barcode: string): { valid: boolean; error?: string } {
  // Regex pattern: R[YY][B][MM][C]M[CS1][CS2]
  const regex = /^R\d{2}\d\d{2}\d{2}M\d{2}\d{2}$/;
  
  if (!regex.test(barcode)) {
    return {
      valid: false,
      error: 'تنسيق الباركود غير صحيح',
    };
  }
  
  return { valid: true };
}

/**
 * Parse barcode into components
 */
function parseBarcode(barcode: string) {
  return {
    year: barcode.substring(1, 3),
    singleDigit: barcode.substring(3, 4),
    month: barcode.substring(4, 6),
    biDigit: barcode.substring(6, 8),
    checksum1: barcode.substring(9, 11),
    checksum2: barcode.substring(11, 13),
  };
}

/**
 * Calculate checksums based on logic from app/lib/barcode.server.ts
 */
function calculateChecksums(year: string, singleDigit: string, month: string, biDigit: string) {
  const yearNum = parseInt(year, 10);
  const monthNum = parseInt(month, 10);
  const singleNum = parseInt(singleDigit, 10); // Random B
  const biNum = parseInt(biDigit, 10);         // Random C
  
  if (monthNum === 0) {
    throw new Error('Month cannot be zero (division error)');
  }
  
  // Series = floor(YY / MM)
  const series = Math.floor(yearNum / monthNum);
  
  // CS1 = (B * C * Series) % 100
  const cs1Val = Math.floor((singleNum * biNum * series) % 100);
  
  // CS2 = (B + C + Series) % 100
  const cs2Val = Math.floor((singleNum + biNum + series) % 100);
  
  const cs1 = String(cs1Val).padStart(2, '0');
  const cs2 = String(cs2Val).padStart(2, '0');
  
  return { cs1, cs2 };
}

/**
 * Validate barcode checksum
 */
function validateChecksum(components: ReturnType<typeof parseBarcode>): { valid: boolean; error?: string } {
  try {
    const { cs1, cs2 } = calculateChecksums(
      components.year,
      components.singleDigit,
      components.month,
      components.biDigit
    );
    
    if (components.checksum1 !== cs1 || components.checksum2 !== cs2) {
      return {
        valid: false,
        error: `الباركود غير صحيح. تحقق من الأرقام (متوقع: ${cs1}${cs2})`,
      };
    }
    
    return { valid: true };
  } catch (error) {
    return {
      valid: false,
      error: 'خطأ في التحقق من الباركود',
    };
  }
}

/**
 * Main validation function (format + checksum)
 */
export function validateBarcode(barcode: string): BarcodeValidationResult {
  // Step 1: Normalize input
  const normalized = barcode.toUpperCase().trim();
  
  // Step 2: Format validation
  const formatCheck = validateFormat(normalized);
  if (!formatCheck.valid) {
    return formatCheck;
  }
  
  // Step 3: Parse components
  const components = parseBarcode(normalized);
  
  // Step 4: Checksum validation
  const checksumCheck = validateChecksum(components);
  if (!checksumCheck.valid) {
    return checksumCheck;
  }
  
  return {
    valid: true,
    components,
  };
}

/**
 * Generate user-friendly error messages in Arabic
 */
export function getValidationMessage(result: BarcodeValidationResult): string {
  if (result.valid) {
    return '✓ باركود صحيح';
  }
  return result.error || 'باركود غير صحيح';
}
